'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */
/* eslint-disable tailwindcss/no-custom-classname */
import { useCalculatedAttributeResolver } from '@hyphengroup/cms-components/calculatedAttributes/useCalculatedAttributeResolver';
import {
  matchApplyV3ModalIntent,
  useApplyV3Modal,
  useCtaUrl,
} from '@hyphengroup/cms-components/components/ApplyCta';
import { CmsImage } from '@hyphengroup/cms-components/components/CmsImage';
import { RichText } from '@hyphengroup/cms-components/components/RichText';
import Tooltip from '@hyphengroup/cms-components/components/Tooltip';
import { useCmsClientContext } from '@hyphengroup/cms-components/utils/cmsPageContext';
import { formatAttributeItemValue } from '@hyphengroup/cms-components/utils/helpers';
import { getApplyButtonLabel } from '@hyphengroup/cms-components/utils/helpers/router';
import { getClientTranslations } from '@hyphengroup/cms-components/utils/translations/getClientTranslations';
import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { staticText } from 'utils/staticText';
import { MiniProductHighlightRating } from './MiniProductHighlightRating';
import {
  CmsProduct,
  EnumMiniProductHighlightBadgeColor,
  SystemAttribute,
} from '$/(cms)/_modules/cmsService/types';
import Button from '$/components/Button';
import { tr } from '$/components/cms/helpers';
import {
  ComponentTypeEnum,
  sendProductAnalytics,
  UserActionEnum,
} from '$/components/cms/helpers/sendAnalytics';
import { useProductDetailModal } from '$/components/cms/helpers/useProductDetailModal';
import { sessionAttributionTracker } from '$/modules/sessionAttributionTracking/sessionAttributionTracker';

interface MiniProductHighlightProps {
  section_title?: string;
  content_before_highlight?: string;
  content_with_highlight?: string;
  product?: CmsProduct;
  attribute?: SystemAttribute;
  mini_product_highlight_attribute_list?: Array<{
    id?: string;
    attribute?: SystemAttribute;
  }>;
  badge?: string;
  badge_colour?: string;
  highlight_first_attribute?: boolean;
  toggle_seedly_rating?: boolean;
  content_after_highlight?: string;
}

const Translations = staticText({
  view_details: {
    _: 'View Details',
    zh: '查看詳情',
  },
});

export const MiniProductHighlight: React.FC<MiniProductHighlightProps> = ({
  section_title,
  content_after_highlight,
  content_before_highlight,
  content_with_highlight,
  product,
  attribute,
  mini_product_highlight_attribute_list,
  badge,
  badge_colour,
  highlight_first_attribute = false,
  toggle_seedly_rating,
}) => {
  const { lang, brand } = useCmsClientContext();
  const t = getClientTranslations(Translations, { lang, brand });
  const { openProductDetail, productDetailModal } = useProductDetailModal({
    componentUniqueKey: `MiniProductHighlight_${product?.uid ?? ''}`,
  });

  const openModal = useCallback(
    (shouldScroll: boolean) => {
      if (!product?.url_alias) {
        return;
      }

      openProductDetail(product, shouldScroll ? 'exclusiveOffers' : null);
    },
    [product, openProductDetail],
  );

  const attributeList = useMemo(() => {
    const list =
      mini_product_highlight_attribute_list
        ?.map((item) => item?.attribute)
        .filter(Boolean) ?? [];
    if (list.length > 0) return list;
    return attribute ? [attribute] : [];
  }, [mini_product_highlight_attribute_list, attribute]);

  const resolveCalculated = useCalculatedAttributeResolver();

  const resolvedAttributes = useMemo(() => {
    const productAttributes = product?.attributes ?? [];
    return attributeList
      .map((attr) => {
        const match = productAttributes.find((productAttr) => {
          if (attr?.uid) {
            return productAttr?.name?.uid === attr.uid;
          }
          return productAttr?.name?.name === attr?.name;
        });

        if (!match) return null;

        const label = match.label || attr?.label || attr?.name || '';
        // A calculated attribute carries no catalogue value — the engine has
        // the figure, already formatted (CMS-1890).
        const calculated = resolveCalculated(match.name?.name, product);
        const value = calculated
          ? calculated.state === 'resolved'
            ? calculated.value.formatted
            : ''
          : (formatAttributeItemValue(match) ?? '');
        const tooltip =
          (match as { additional_information?: string })
            .additional_information || '';

        return {
          id: attr?.uid ?? attr?.name ?? label,
          label,
          value,
          tooltip,
        };
      })
      .filter((item): item is NonNullable<typeof item> => Boolean(item))
      .slice(0, 4);
  }, [attributeList, product, resolveCalculated]);

  const { open: openApplyV3Modal } = useApplyV3Modal();
  const redirectUrl = useCtaUrl({ product }) ?? '';

  const applyLabel = useMemo(() => {
    return getApplyButtonLabel(
      tr('apply_now', lang),
      undefined,
      product?.call_to_action,
    );
  }, [lang, product?.call_to_action]);

  const hasBadge = Boolean(badge?.trim());
  const hasProductDetails = Boolean(product?.url_alias);

  const badgeColorClasses = classNames({
    'bg-highlights/blue-light text-highlights/blue-dark':
      !badge_colour || badge_colour === EnumMiniProductHighlightBadgeColor.Blue,
    'bg-highlights/red-light text-highlights/red-dark':
      badge_colour === EnumMiniProductHighlightBadgeColor.Red,
    'bg-highlights/green-light text-highlights/green-dark':
      badge_colour === EnumMiniProductHighlightBadgeColor.Green,
    'bg-highlights/yellow-light text-highlights/yellow-dark':
      badge_colour === EnumMiniProductHighlightBadgeColor.Yellow,
    'bg-highlights/orange-light text-highlights/orange-dark':
      badge_colour === EnumMiniProductHighlightBadgeColor.Orange,
  });

  const handleApplyClick = useCallback(() => {
    if (!redirectUrl) return;
    sessionAttributionTracker.recordApplyClick();
    sendProductAnalytics({
      action: UserActionEnum.CLICK,
      componentTitle: 'Product Card/CTA_Apply Now',
      componentType: ComponentTypeEnum.PRODUCT_CARD,
      event: 'product_click_apply',
      index: '0',
      product: product as CmsProduct,
    });

    const modalIntent = matchApplyV3ModalIntent(redirectUrl);
    if (modalIntent) {
      openApplyV3Modal(modalIntent);
      return;
    }

    window.open(redirectUrl, '_blank');
  }, [openApplyV3Modal, product, redirectUrl]);

  const handleViewDetailsClick = useCallback(() => {
    if (!product?.url_alias) return;
    sendProductAnalytics({
      action: UserActionEnum.CLICK,
      componentTitle: 'Product Card/View Details',
      componentType: ComponentTypeEnum.PRODUCT_CARD,
      event: 'click',
      index: '0',
      product: product as CmsProduct,
    });
    openModal(true);
  }, [openModal, product]);

  const productMediaSrc =
    product?.product_image || product?.provider?.provider_image || '';

  return (
    <section className="flex flex-col gap-4 sm:gap-6">
      {section_title && (
        <h2 className="mhg-alpha-typography-display-1 text-text/primary">
          {section_title}
        </h2>
      )}

      {content_before_highlight && (
        <RichText className="text-text/secondary" baseTextStyle="Body2Relaxed">
          {content_before_highlight}
        </RichText>
      )}

      <div className="flex flex-col gap-spacing/padding/section-x md:flex-row">
        <RichText
          className="text-text/secondary sm:flex-1"
          baseTextStyle="Body2Relaxed"
        >
          {content_with_highlight}
        </RichText>

        {product && (
          <div className="order-first flex flex-col gap-spacing/gap/soft-12px overflow-hidden rounded-radius/card border border-borders/default p-spacing/padding/soft-12px bg-white sm:flex-1 md:order-none md:max-w-[--general-content-panel] md:self-start">
            {hasBadge && (
              <div className="flex justify-center">
                <div
                  className={`w-full rounded-radius/button px-spacing/padding/soft-8px py-spacing/padding/soft-4px ${badgeColorClasses}`}
                >
                  <h4 className="mhg-alpha-typography-bullet-2">{badge}</h4>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-spacing/gap/soft-12px">
              <div className="flex items-center gap-spacing/gap/soft-12px">
                {productMediaSrc && (
                  <div className="flex h-16 w-[101px] shrink-0 items-center justify-center rounded-[4px]">
                    <CmsImage
                      unoptimized
                      src={productMediaSrc}
                      alt={product.product_name || 'Product image'}
                      width={101}
                      height={64}
                      className="size-full cursor-pointer object-contain"
                      onClick={() => openModal(false)}
                    />
                  </div>
                )}

                <div className="flex w-full flex-col gap-spacing/gap/soft-6px">
                  <h3 className="mhg-alpha-typography-bullet-2 text-text/primary">
                    {product.product_name}
                  </h3>

                  {brand === 'singsaver' &&
                    toggle_seedly_rating === true &&
                    product?.rate_score && (
                      <MiniProductHighlightRating
                        score={product?.rate_score}
                        info={product?.rate_additional_information}
                      />
                    )}
                </div>
              </div>

              {resolvedAttributes.length > 0 && (
                <div className="grid grid-cols-2 gap-spacing/gap/soft-12px">
                  {resolvedAttributes.map((attr, index) => {
                    const isHighlighted =
                      highlight_first_attribute && index === 0;
                    return (
                      <div
                        key={attr.id}
                        className="flex flex-col gap-spacing/gap/soft-4px"
                      >
                        <p className="mhg-alpha-typography-caption text-text/secondary-lighter">
                          {attr.label}
                        </p>
                        <div className="flex items-center gap-spacing/gap/soft-4px">
                          {isHighlighted ? (
                            <span className="mhg-alpha-typography-bullet-2 rounded-radius/tooltip px-spacing/padding/soft-8px py-spacing/padding/soft-4px bg-fill/primary-light text-text/accent">
                              {attr.value}
                            </span>
                          ) : (
                            <p className="mhg-alpha-typography-bullet-2 text-text/primary">
                              {attr.value}
                            </p>
                          )}
                          {attr.tooltip && (
                            <Tooltip
                              infoClass="!size-[--shapes-icon-size-sm] text-text/disabled"
                              content={attr.tooltip}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex w-full gap-spacing/gap/soft-8px">
                {hasProductDetails && (
                  <Button
                    size="small"
                    variant="outline"
                    styleType="neutral"
                    className="flex-1"
                    onClick={handleViewDetailsClick}
                  >
                    {t.view_details}
                  </Button>
                )}
                {redirectUrl && (
                  <Button
                    size="small"
                    variant="outline"
                    styleType="cta"
                    className="flex-1"
                    onClick={handleApplyClick}
                  >
                    {applyLabel}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {content_after_highlight && (
        <RichText className="text-text/secondary" baseTextStyle="Body2Relaxed">
          {content_after_highlight}
        </RichText>
      )}
      {productDetailModal}
    </section>
  );
};
